import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DEFAULT_VIDEO_EFFECTS, EFFECT_CONTROLS, type VideoEffects } from '@/lib/video-effects/types';
import { buildLutCards, type LutCard } from '@/lib/video-effects/lutCards';
import { LutCarousel } from '@/components/video-effects/LutCarousel';
import { useLutThumbnails } from '@/hooks/use-lut-thumbnails';

/** The panel edits the clip's BASE look; timeline sections stack on top of it
 *  and are managed in the timeline, so a reset keeps the sections. */
const resetBaseLook = (value: VideoEffects): VideoEffects =>
  ({ ...DEFAULT_VIDEO_EFFECTS, ...(value.sections?.length ? { sections: value.sections } : {}) });
/** The panel has no on/off switch: every edit saves the base look as enabled,
 *  so a clip saved earlier with the switch off turns back on when edited. */
const enabledLook = (next: VideoEffects): VideoEffects => (next.enabled ? next : { ...next, enabled: true });
type Props = {
  value: VideoEffects; onChange: (value: VideoEffects) => void;
  onPreview: (value: VideoEffects | null) => void;
  disabled?: boolean; saveError?: boolean; onRetry: () => void;
  /** Current clip frame (data URL) the LUT thumbnails are rendered from. */
  thumbnailFrame?: string | null;
};
export function VideoEffectsPanel({ value, onChange, onPreview, disabled, saveError, onRetry, thumbnailFrame }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const dragging = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => { setDraft(value); }, [value]);
  const lutCards = useMemo(() => buildLutCards(draft), [draft]);
  const lutThumbnails = useLutThumbnails(thumbnailFrame, lutCards);
  const selectLut = (card: LutCard) => {
    if (card.kind === 'original') commit({ ...value, lut: null });
    else if (card.kind === 'preset' && card.presetId) commit({ ...value, lut: { preset: card.presetId, strength: value.lut?.strength ?? 1 } });
  };
  const commit = (edit: VideoEffects) => { const next = enabledLook(edit); setDraft(next); onChange(next); onPreview(null); };
  const preview = (edit: VideoEffects) => { const next = enabledLook(edit); setDraft(next); onPreview(next); };
  const cancelPreview = () => { dragging.current = false; setDraft(valueRef.current); onPreview(null); };
  const sliderGestures = {
    onPointerDownCapture: () => { dragging.current = true; },
    // Radix releases capture before invoking onValueCommit on pointerup.
    onPointerUpCapture: () => { dragging.current = false; },
    onPointerCancel: cancelPreview,
    onLostPointerCapture: () => { if (dragging.current) cancelPreview(); },
  };
  return <Card className="p-3 space-y-3" data-testid="video-effects-panel">
    <h3 className="flex items-center gap-2 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4 text-primary" />{t('videoEffects.title')}</h3>
    <p className="text-xs text-muted-foreground">{t('videoEffects.description')}</p>
    {saveError && <div role="alert" className="text-xs text-destructive">{t('videoEffects.saveError')} <Button variant="link" size="sm" onClick={onRetry}>{t('videoEffects.retry')}</Button></div>}
    <fieldset disabled={disabled} className="min-w-0 space-y-3">
      <Tabs defaultValue="looks">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="looks">{t('videoEffects.looks')}</TabsTrigger>
          <TabsTrigger value="adjust">{t('videoEffects.adjust')}</TabsTrigger>
          <TabsTrigger value="visual">{t('videoEffects.visual')}</TabsTrigger>
        </TabsList>
        <TabsContent value="looks" className="space-y-3">
          <LutCarousel cards={lutCards} thumbnails={lutThumbnails} onSelect={selectLut} disabled={disabled} />
          {draft.lut && <div className="space-y-2">
            <div className="flex justify-between text-xs"><Label htmlFor="lut-strength" className="text-xs">{t('videoEffects.strength')}</Label><output>{Math.round(draft.lut.strength * 100)}%</output></div>
            <Slider {...sliderGestures} disabled={disabled} thumbProps={{ 'aria-label': t('videoEffects.strength') }} id="lut-strength" aria-label={t('videoEffects.strength')} min={0} max={100} step={1} value={[Math.round(draft.lut.strength * 100)]}
              onValueChange={([strength]) => preview({ ...draft, lut: { ...draft.lut!, strength: strength / 100 } })}
              onValueCommit={([strength]) => commit({ ...draft, lut: { ...draft.lut!, strength: strength / 100 } })} />
          </div>}
        </TabsContent>
        {(['adjust', 'visual'] as const).map(group => <TabsContent key={group} value={group} className="space-y-4 pt-2">
          {EFFECT_CONTROLS.filter(control => control.group === group).map(({ key, min, max, step }) => <div key={key} className="space-y-2">
            <div className="flex items-center justify-between"><Label htmlFor={`effect-${key}`} className="text-xs">{t(`videoEffects.controls.${key}`)}</Label>
              <button type="button" className="text-xs tabular-nums text-muted-foreground hover:text-foreground" title={t('videoEffects.resetControl')} aria-label={`${t('videoEffects.resetControl')}: ${t(`videoEffects.controls.${key}`)}`} onClick={() => commit({ ...value, [key]: 0 })}>{Number(draft[key].toFixed(2))}{key === 'exposure' ? ' EV' : ''}</button></div>
            <Slider {...sliderGestures} disabled={disabled} thumbProps={{ 'aria-label': t(`videoEffects.controls.${key}`) }} id={`effect-${key}`} aria-label={t(`videoEffects.controls.${key}`)} min={min} max={max} step={step} value={[draft[key]]}
              onValueChange={([amount]) => preview({ ...draft, [key]: amount })}
              onValueCommit={([amount]) => commit({ ...draft, [key]: amount })} />
          </div>)}
        </TabsContent>)}
      </Tabs>
      <div className="flex justify-end border-t pt-3">
        <Button variant="ghost" size="sm" aria-label={t('videoEffects.reset')} title={t('videoEffects.reset')} onClick={() => commit(resetBaseLook(value))}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </div>
    </fieldset>
  </Card>;
}
